import React from 'react';

import * as analytics from 'analytics/Analytics';
import Banner from 'buffers/Banner';
import DisplayOptions from 'components/DisplayOptions';
import {VisibilityOptions} from 'components/VisibilityOptions';
import DataConnector from 'connect/DataConnector';
import {AlignmentPolymerType, ProteinAlignment} from 'constants/AlignmentConstants';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import AlignmentPDFDownloadModal from 'molbio/AlignmentPDFDownloadModal';
import {generateRawSequencesFromProteinAlignment} from 'molbio/AlignmentPDFHelpers/GenerateRawSequencesFromAlignment';
import {getAntibodyVisibilitySettings} from 'molbio/antibody-engineering/visibilityHelpers';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ModalAnchor from 'ui-library/ModalAnchor';
import runModal from 'ui-library/runModal';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {textToDataURI} from 'util/TextToBlobUtil';

import ProteinStore from '../ProteinStore';
import ExportPNGModal from './ExportPNGModal';
import {fastaFormat} from './ProteinAlignmentUtil';
import ProteinRealignModal from './ProteinRealignModal';

interface ConnectedProps {
  allowFeatureEdits?: boolean;
}

class ProteinAlignmentToolbarConnector extends DataConnector<ProteinAlignmentToolbarProps, ConnectedProps> {
  shouldSubscribeToRedux = false;
  stores = [ProteinStore];

  enhanceProps(): ConnectedProps {
    const protein = this.proteinStore().getProteinById(this.props.alignment.proteinId);
    return {
      allowFeatureEdits: protein && protein.permissions.polymerFeaturesEditable!,
    };
  }
}

interface ProteinAlignmentToolbarProps {
  visibilityOptions: VisibilityOptions;
  alignment: ProteinAlignment;
  renderAlignmentSVG: () => Promise<SVGSVGElement>;
  selection: {
    start: number;
    end: number | null;
  };
}

interface State {
  isExportModalOpen: boolean;
  isRealignModalOpen: boolean;
}

export class ProteinAlignmentToolbar extends React.Component<
  ProteinAlignmentToolbarProps & ConnectedProps,
  State
> {
  state: State = {
    isExportModalOpen: false,
    isRealignModalOpen: false,
  };

  _cachedFastaURI?: string;
  _cachedAlignment?: ProteinAlignment;

  getCachedFastaURI(): string | undefined {
    if (this._cachedAlignment !== this.props.alignment) {
      this._cachedAlignment = this.props.alignment;
      this._cachedFastaURI = textToDataURI(fastaFormat(this.props.alignment));
    }
    return this._cachedFastaURI;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleRealign = () => {
    this.setState({isRealignModalOpen: true});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCloseRealignModal = () => {
    this.setState({isRealignModalOpen: false});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleExportPNG = () => {
    this.setState({isExportModalOpen: true});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleCloseExportModal = () => {
    this.setState({isExportModalOpen: false});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleExportPDF = async () => {
    const rawAlignmentData = await generateRawSequencesFromProteinAlignment(
      this.props.alignment,
      this.props.visibilityOptions
    );
    analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_FORMATTED_PDF_MODAL, {
      residueType: AlignmentPolymerType.AA,
    });
    runModal(AlignmentPDFDownloadModal, {
      rawAlignmentData,
      alignmentPolymerType: AlignmentPolymerType.AA,
      selection: this.props.selection,
      alignmentName: this.props.alignment.name,
      cdrDefinition: getAntibodyVisibilitySettings(this.props.visibilityOptions)[1],
    });
  };

  render(): JSX.Element {
    const alignmentUrl = `${window.location.href}?alignment=${this.props.alignment.id}`;
    return (
      <div>
        <ModalAnchor open={this.state.isExportModalOpen}>
          <ExportPNGModal
            title="Export protein alignment"
            downloadName={`${this.props.alignment.name}.png`}
            onRequestClose={this._handleCloseExportModal}
            renderSVG={this.props.renderAlignmentSVG}
          />
        </ModalAnchor>
        <ModalAnchor open={this.state.isRealignModalOpen}>
          <ProteinRealignModal
            alignment={this.props.alignment}
            onRequestClose={this._handleCloseRealignModal}
          />
        </ModalAnchor>
        <div className="navbar nav buffer-options-container">
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={!this.props.allowFeatureEdits}
            onClick={this._handleRealign}
            size={ButtonSize.MINI}
            text="Realign"
          />
          <div className="buffer-options-item--bannerContainer buffer-options-item--proteinBannerContainer">
            <Banner message="Protein alignments are read-only." />
          </div>
          <div className="buttonGroup">
            <Tooltip container="body" placement={TooltipPlacement.TOP} title="Export">
              <div className="buttonGroup">
                <Dropdown
                  menu={
                    <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
                      <Option download={`${this.props.alignment.name}.fasta`} href={this.getCachedFastaURI()}>
                        FASTA
                      </Option>
                      <Option onSelect={this._handleExportPNG}>PNG</Option>
                      <Option onSelect={this._handleExportPDF}>PDF</Option>
                      <Option
                        onSelect={() => {
                          copyToClipboard(alignmentUrl, {
                            showToastOnSuccess: true,
                            onSuccess: () => {
                              analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.COPIED_ALIGNMENT_URL, {
                                residueType: AlignmentPolymerType.AA,
                              });
                            },
                          });
                        }}
                      >
                        <div>Copy Link</div>
                      </Option>
                    </Menu>
                  }
                >
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_GRAY}
                    icon="export"
                    isDropdown={true}
                    size={ButtonSize.MINI}
                  />
                </Dropdown>
              </div>
            </Tooltip>
            <DisplayOptions options={this.props.visibilityOptions} />
          </div>
        </div>
      </div>
    );
  }
}

export default ProteinAlignmentToolbarConnector.connect(ProteinAlignmentToolbar);
